
import { useState, useEffect } from 'react';
import { Card } from "@/components/ui/card";
import { Check, Star, ArrowRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getFamilyCounter } from '@/utils/familyCounter';
import { cn } from '@/lib/utils';

interface CareOption {
  title: string;
  cost: string;
  period: string;
  features: string[];
  drawbacks: string[];
  recommended: boolean;
  savings: string;
  badge?: string;
}

const CostComparison = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [familyCount, setFamilyCount] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('cost-comparison');
      if (element) {
        const position = element.getBoundingClientRect();
        if (position.top < window.innerHeight * 0.75) {
          setIsVisible(true);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Get the counter when component mounts
    const counter = getFamilyCounter();
    setFamilyCount(counter.count);
  }, []);

  const options: CareOption[] = [
    {
      title: "Nursing Home",
      cost: "$94,900 - $108,000",
      period: "annually",
      features: ["24/7 Care", "Meals & Housekeeping", "Medical Staff on Site"],
      drawbacks: ["Loss of Independence", "Institutional Environment", "Rigid Schedules"],
      recommended: false,
      savings: "Save 30-40% with FlowCare"
    },
    {
      title: "Assisted Living",
      cost: "$54,000+",
      period: "annually",
      features: ["Partial Care Support", "Community Activities", "Some Meal Service"],
      drawbacks: ["Limited Care Hours", "Less Privacy", "Additional Fees for Care"],
      recommended: false,
      savings: "Save 30-40% with FlowCare"
    },
    {
      title: "Traditional Agency",
      cost: "3-4 Hour",
      period: "minimum visits",
      features: ["In-Home Service", "Scheduled Visits", "Basic Care Options"],
      drawbacks: ["High Hourly Minimums", "Inconsistent Caregivers", "Frequent No-Shows"],
      recommended: false,
      savings: "Save 30-40% with FlowCare"
    },
    {
      title: "FlowCare",
      cost: "No Minimum",
      period: "flexible visits",
      features: [
        "Dedicated Care Team", 
        "No Hourly Minimums", 
        "Reliable & Consistent", 
        "Tech-Enhanced Care", 
        "Aging in Place",
        "Customized Care Plans"
      ],
      drawbacks: [],
      recommended: true,
      savings: ""
    }
  ];

  return (
    <section id="cost-comparison" className="py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold text-abbella-ebony mb-6 transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              Care That Fits Your Budget
            </h2>
            <p className={`text-lg text-slate-600 max-w-3xl mx-auto transition-all duration-700 delay-100 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              Traditional care solutions are expensive and often include costs for services you don't need. 
              See how FlowCare's flexible approach can save you 30-40% while providing superior care.
            </p>
            <div className={`mt-6 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              <Badge variant="outline" className={cn(
                "text-sm font-medium py-1.5 px-3 bg-gradient-to-r from-abbella-aqua/10 to-abbella-aqua/20 border-abbella-aqua/30",
                "inline-flex items-center justify-center gap-1"
              )}>
                <span className="text-abbella-ebony">Helping</span>
                <span className="text-abbella-aqua font-bold">{familyCount}</span>
                <span className="text-abbella-ebony">families this month</span>
              </Badge>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {options.map((option, index) => (
              <div 
                key={option.title}
                className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ transitionDelay: `${(index + 1) * 150}ms` }}
              >
                <Card className={`h-full p-6 border ${option.recommended ? 'border-abbella-aqua shadow-lg ring-2 ring-abbella-aqua/20' : 'border-gray-200'}`}>
                  <div className="flex flex-col h-full">
                    {option.recommended && !option.badge && (
                      <div className="bg-abbella-aqua/10 text-abbella-aqua text-sm font-medium px-3 py-1 rounded-full self-start mb-3">
                        Recommended
                      </div>
                    )}
                    
                    {option.badge && (
                      <div className="flex items-center gap-1.5 bg-green-100 text-green-700 text-sm font-medium px-3 py-1 rounded-full self-start mb-3">
                        <Star className="h-3.5 w-3.5" fill="currentColor" />
                        {option.badge}
                      </div>
                    )}
                    
                    <h3 className="text-xl font-bold text-abbella-ebony mb-2">{option.title}</h3>
                    
                    <div className="mb-4">
                      <div className="text-2xl font-bold text-abbella-ebony">{option.cost}</div>
                      <div className="text-sm text-slate-500">{option.period}</div>
                    </div>
                    
                    <div className="mb-4 flex-grow">
                      <p className="text-sm font-medium text-abbella-ebony mb-2">Includes:</p>
                      <ul className="space-y-2">
                        {option.features.map((feature) => (
                          <li key={feature} className="flex items-start text-sm">
                            <Check className="w-4 h-4 text-abbella-aqua flex-shrink-0 mt-0.5 mr-2" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {option.drawbacks && option.drawbacks.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-abbella-ebony mb-2">Drawbacks:</p>
                        <ul className="space-y-2">
                          {option.drawbacks.map((drawback) => (
                            <li key={drawback} className="flex items-start text-sm text-slate-600">
                              <span className="text-red-500 mr-2">•</span>
                              <span>{drawback}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {option.savings ? (
                      <div className="mt-auto pt-4 border-t border-gray-100">
                        <p className="text-sm font-medium text-green-600">{option.savings}</p>
                      </div>
                    ) : option.recommended && (
                      <div className="mt-auto pt-4 border-t border-gray-100">
                        <Button 
                          variant="default" 
                          className="w-full bg-abbella-aqua hover:bg-abbella-aqua/90 text-white"
                          onClick={() => {
                            const ctaSection = document.getElementById('call-to-action');
                            if (ctaSection) {
                              ctaSection.scrollIntoView({ behavior: 'smooth' });
                            }
                          }}
                        >
                          Get Started <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CostComparison;
